import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Collapse,
  Chip,
  Divider,
  Paper,
  Tooltip,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import VideocamIcon from '@mui/icons-material/Videocam';
import MicIcon from '@mui/icons-material/Mic';
import SpeedIcon from '@mui/icons-material/Speed';
import useRoomStats from '../../hooks/useRoomStats/useRoomStats';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import useParticipantNetworkQualityLevel from '../../hooks/useParticipantNetworkQualityLevel/useParticipantNetworkQualityLevel';
import { Participant, RemoteParticipant } from 'twilio-video';

const OverlayContainer = styled(Paper)(({ theme }) => ({
  position: 'fixed',
  top: theme.headerHeight ? `${theme.headerHeight + 16}px` : '80px',
  left: '16px',
  zIndex: 1300,
  backgroundColor: 'rgba(0, 0, 0, 0.85)',
  color: 'white',
  borderRadius: '12px',
  padding: '12px',
  minWidth: '280px',
  maxWidth: '350px',
  maxHeight: 'calc(100vh - 200px)',
  overflowY: 'auto',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.1)',
  [theme.breakpoints.down('sm')]: {
    left: '8px',
    right: '8px',
    maxWidth: 'none',
    minWidth: 'auto',
  },
}));

const Header = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '8px',
});

const Title = styled(Typography)({
  fontWeight: 600,
  fontSize: '0.9rem',
  display: 'flex',
  alignItems: 'center',
  gap: '6px',
});

const StatRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '4px 0',
});

const StatLabel = styled(Typography)({
  fontSize: '0.75rem',
  color: 'rgba(255, 255, 255, 0.7)',
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
});

const StatValue = styled(Typography)<{ quality?: 'excellent' | 'good' | 'fair' | 'poor' }>(
  ({ quality }) => ({
    fontSize: '0.85rem',
    fontWeight: 500,
    fontFamily: 'monospace',
    color:
      quality === 'excellent'
        ? '#4caf50'
        : quality === 'good'
          ? '#8bc34a'
          : quality === 'fair'
            ? '#ff9800'
            : quality === 'poor'
              ? '#f44336'
              : 'white',
  })
);

const QualityChip = styled(Chip)<{ quality: 'excellent' | 'good' | 'fair' | 'poor' }>(
  ({ quality }) => ({
    height: '20px',
    fontSize: '0.65rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    backgroundColor:
      quality === 'excellent'
        ? '#4caf50'
        : quality === 'good'
          ? '#8bc34a'
          : quality === 'fair'
            ? '#ff9800'
            : '#f44336',
    color: 'white',
  })
);

const ParticipantSection = styled(Box)({
  marginTop: '8px',
  padding: '8px',
  backgroundColor: 'rgba(255, 255, 255, 0.05)',
  borderRadius: '8px',
});

const ParticipantName = styled(Typography)({
  fontSize: '0.8rem',
  fontWeight: 500,
  marginBottom: '4px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
});

interface DiagnosticsOverlayProps {
  open: boolean;
  onClose: () => void;
}

function getQualityFromPacketLoss(packetLoss: number): 'excellent' | 'good' | 'fair' | 'poor' {
  if (packetLoss <= 0.5) return 'excellent';
  if (packetLoss <= 2) return 'good';
  if (packetLoss <= 5) return 'fair';
  return 'poor';
}

function getQualityFromRtt(rtt: number): 'excellent' | 'good' | 'fair' | 'poor' {
  if (rtt <= 100) return 'excellent';
  if (rtt <= 200) return 'good';
  if (rtt <= 400) return 'fair';
  return 'poor';
}

// Convert Twilio's 1-5 network quality level to our quality labels
function getQualityFromLevel(level: number | null): 'excellent' | 'good' | 'fair' | 'poor' {
  if (level === null) return 'good'; // Default when unknown
  if (level >= 4) return 'excellent';
  if (level === 3) return 'good';
  if (level === 2) return 'fair';
  return 'poor';
}

function formatBitrate(bitrate: number): string {
  if (bitrate >= 1000) {
    return `${(bitrate / 1000).toFixed(1)} Mbps`;
  }
  return `${bitrate} kbps`;
}

// Component to display participant quality using Twilio's network quality
function ParticipantQualityChip({ participant }: { participant: Participant }) {
  const networkQualityLevel = useParticipantNetworkQualityLevel(participant);
  const quality = getQualityFromLevel(networkQualityLevel);
  const label = networkQualityLevel !== null ? `${quality} (${networkQualityLevel}/5)` : quality;

  return <QualityChip quality={quality} label={label} size="small" />;
}

export default function DiagnosticsOverlay({ open, onClose }: DiagnosticsOverlayProps) {
  const { room } = useVideoContext();
  const roomStats = useRoomStats(2000);
  const [showParticipants, setShowParticipants] = useState(false);

  if (!open || !roomStats || !room) return null;

  const { aggregate, localParticipant, remoteParticipants } = roomStats;

  // Get remote participants from room for quality levels
  const remoteParticipantsList = Array.from(room.participants.values());

  return (
    <OverlayContainer elevation={8}>
      <Header>
        <Title>
          <SpeedIcon sx={{ fontSize: '1rem' }} />
          Live Diagnostics
        </Title>
        <IconButton size="small" onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Header>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', mb: 1 }} />

      {/* Aggregate Stats */}
      <Box>
        <StatRow>
          <StatLabel>
            <SignalCellularAltIcon sx={{ fontSize: '0.9rem' }} />
            Total Bitrate
          </StatLabel>
          <StatValue>{formatBitrate(aggregate.totalBitrate)}</StatValue>
        </StatRow>

        <StatRow>
          <StatLabel>Packet Loss</StatLabel>
          <StatValue quality={getQualityFromPacketLoss(aggregate.avgPacketLoss)}>
            {aggregate.avgPacketLoss.toFixed(2)}%
          </StatValue>
        </StatRow>

        <StatRow>
          <StatLabel>Latency (RTT)</StatLabel>
          <StatValue quality={getQualityFromRtt(aggregate.avgRoundTripTime)}>
            {aggregate.avgRoundTripTime > 0 ? `${aggregate.avgRoundTripTime} ms` : 'N/A'}
          </StatValue>
        </StatRow>

        <StatRow>
          <StatLabel>Jitter</StatLabel>
          <StatValue quality={aggregate.avgJitter > 50 ? 'fair' : 'good'}>
            {aggregate.avgJitter > 0 ? `${aggregate.avgJitter} ms` : 'N/A'}
          </StatValue>
        </StatRow>
      </Box>

      <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.1)', my: 1 }} />

      {/* Participants Toggle */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          py: 0.5,
        }}
        onClick={() => setShowParticipants(!showParticipants)}
      >
        <Typography sx={{ fontSize: '0.8rem', fontWeight: 500 }}>
          Participants ({1 + remoteParticipants.length})
        </Typography>
        <IconButton size="small" sx={{ color: 'white' }}>
          {showParticipants ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={showParticipants}>
        {/* Local Participant */}
        {localParticipant && (
          <ParticipantSection>
            <ParticipantName>
              <span>You (Local)</span>
              <ParticipantQualityChip participant={room.localParticipant} />
            </ParticipantName>
            {localParticipant.tracks.length > 0 ? (
              localParticipant.tracks.map(track => (
                <StatRow key={track.trackId}>
                  <StatLabel>
                    {track.trackType === 'video' ? (
                      <VideocamIcon sx={{ fontSize: '0.8rem' }} />
                    ) : (
                      <MicIcon sx={{ fontSize: '0.8rem' }} />
                    )}
                    {track.trackType}
                  </StatLabel>
                  <StatValue>
                    {formatBitrate(track.bitrate)}
                  </StatValue>
                </StatRow>
              ))
            ) : (
              <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>
                Collecting stats...
              </Typography>
            )}
          </ParticipantSection>
        )}

        {/* Remote Participants - filter out chaperoning mode participants */}
        {remoteParticipantsList
          .filter((participant: RemoteParticipant) => !participant.identity.includes('###CP##'))
          .map((participant: RemoteParticipant) => {
            const participantStats = remoteParticipants.find(p => p.participantSid === participant.sid);

            return (
              <ParticipantSection key={participant.sid}>
                <ParticipantName>
                  <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '150px' }}>
                    {participant.identity.split('###')[0]}
                  </span>
                  <ParticipantQualityChip participant={participant} />
                </ParticipantName>
                {participantStats && participantStats.tracks.length > 0 ? (
                  participantStats.tracks.map(track => (
                    <StatRow key={track.trackId}>
                      <StatLabel>
                        {track.trackType === 'video' ? (
                          <VideocamIcon sx={{ fontSize: '0.8rem' }} />
                        ) : (
                          <MicIcon sx={{ fontSize: '0.8rem' }} />
                        )}
                        {track.trackType}
                      </StatLabel>
                      <Tooltip title={`Packet Loss: ${track.packetLossPercentage}%`}>
                        <StatValue quality={getQualityFromPacketLoss(track.packetLossPercentage)}>
                          {formatBitrate(track.bitrate)}
                        </StatValue>
                      </Tooltip>
                    </StatRow>
                  ))
                ) : (
                  <Typography sx={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)' }}>
                    Collecting stats...
                  </Typography>
                )}
              </ParticipantSection>
            );
          })}
      </Collapse>
    </OverlayContainer>
  );
}
